import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import useLocalStorageState from "../hooks/useLocalStorageState";
import { LoginType } from "../types/auth";
import { PastRuns } from "../types/run";
import { IPastRunsContext, PastRunsContext } from "./pastRuns";

interface Props {
  children: JSX.Element | JSX.Element[];
}

/**
 * Single owner of the "pastRuns" local storage state and its cache buster.
 * The cache buster "pastRunsBuster" is set to the current seasonal database.
 * The "pastRuns" are reset when the seasonal database changes on a new season.
 * Cache busting only applies to credentials login.
 */
const PastRunsProvider = ({ children }: Props) => {
  const seasonalDatabase = useSelector(
    (state: { auth: IAuthReducer }) => state.auth.seasonalDatabase
  );
  const loginType = useSelector(
    (state: { auth: IAuthReducer }) => state.auth.loginType
  );

  const [pastRuns, setPastRuns] = useLocalStorageState<PastRuns>(
    "pastRuns",
    JSON.parse(localStorage.getItem("pastRuns") || "[]") as PastRuns
  );

  // Cache buster for "pastRuns", set to the current seasonal database.
  const [pastRunsBuster, setPastRunsBuster] = useLocalStorageState<string>(
    "pastRunsBuster",
    JSON.parse(localStorage.getItem("pastRunsBuster") || '""') as string
  );
  const pastRunsBusterFormatted = pastRunsBuster.replace(/"/g, "");

  useEffect(() => {
    // Cache busting only applies to credentials login, not grower login.
    if (loginType !== LoginType.Credentials) return;
    if (!seasonalDatabase) return;

    if (
      pastRunsBusterFormatted === "" ||
      pastRunsBusterFormatted !== seasonalDatabase
    ) {
      // No buster set, or season changed. Reset the buster and "pastRuns".
      setPastRunsBuster(seasonalDatabase);
      setPastRuns([]);
    }
  }, [
    loginType,
    pastRunsBusterFormatted,
    seasonalDatabase,
    setPastRunsBuster,
    setPastRuns,
  ]);

  const refreshPastRuns = () => {
    setPastRuns(
      JSON.parse(localStorage.getItem("pastRuns") || "[]") as PastRuns
    );
  };

  const value: IPastRunsContext = {
    pastRuns,
    setPastRuns,
    refreshPastRuns,
  };

  return (
    <PastRunsContext.Provider value={value}>
      {children}
    </PastRunsContext.Provider>
  );
};

export default PastRunsProvider;
